import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import moment from "moment";
import {
  border, textPrimary, textSecondary, textMuted, font, success, warning,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { card } from "./recordTheme";
import { formatSubjectName } from "../../../layouts/schoolPortal/schoolPortalTheme";
import { get_student_coaching_api } from "../../../DAL/SchoolPortal/SchoolPortal";

/**
 * The top of a student's record: what is happening that the instructor should
 * know, and the sessions coming up. Brief on purpose — a line each, one small
 * action each. The subjects follow directly underneath.
 * GET /api/school/students/:id/coaching.
 */

const SHORT_CTA = { propose: "Book a session", task: "Set practice", message: "Message" };
const STATUS = {
  booked: { text: "Booked", color: success },
  requested: { text: "Requested — needs you", color: warning },
  offered: { text: "Offered — waiting on them", color: textSecondary },
};

export default function RecordBriefing({ studentId, studentName, onAct, onPropose, onLoaded, onDismiss, openItems = null, refreshKey = 0 }) {
  const [state, setState] = useState({ loading: true, data: null });
  const first = (studentName || "").split(" ")[0] || "this student";

  // LIVE: re-read every minute, whenever the tab comes back into view, and
  // straight after the instructor acts. The server works it out fresh from
  // the student's latest answers each time.
  useEffect(() => {
    let dead = false;
    const load = () => get_student_coaching_api(studentId)
      .then((r) => {
        if (dead || r?.code !== 200) { if (!dead) setState((s) => (s.data ? s : { loading: false, data: null })); return; }
        setState({ loading: false, data: r });
        onLoaded?.(r);
      })
      .catch(() => { if (!dead) setState((s) => (s.data ? s : { loading: false, data: null })); });
    load();
    const timer = setInterval(load, 60000);
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { dead = true; clearInterval(timer); document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("focus", onFocus); };
  }, [studentId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading) {
    return <Box sx={{ ...card, mt: 2, display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={14} /><Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>Loading…</Typography></Box>;
  }
  const d = state.data;
  if (!d) return null;
  const items = (d.actions || []).filter((a) => a.kind !== "prepare").slice(0, 4);
  const prep = (d.actions || []).find((a) => a.kind === "prepare");
  // Older servers do not send `upcoming`; fall back to what the record has.
  const upcoming = d.upcoming || [
    ...(openItems?.nextSession ? [{ id: "next", at: openItems.nextSession.at, subject: openItems.nextSession.subject, subjectName: formatSubjectName(openItems.nextSession.subject || ""), status: "booked" }] : []),
    ...((openItems?.proposals || []).map((p, i) => ({ id: `p${i}`, at: p.startTime || p.at, subject: p.subject, subjectName: formatSubjectName(p.subject || ""), status: "offered" }))),
  ].filter((u) => u.at).sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <Box sx={{ ...card, mt: 2, p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" } }}>
      {/* WORTH KNOWING */}
      <Box sx={{ px: 2.25, py: 1.75 }}>
        <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary }}>Worth knowing</Typography>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, mt: 0.5, mb: 0.75, lineHeight: 1.5 }}>{d.headline}</Typography>
        {items.map((a) => {
          const cta = a.cta?.kind && a.cta.kind !== "prepare" ? a.cta : null;
          return (
            <Box key={a.id} sx={{ display: "grid", gridTemplateColumns: "8px 1fr auto", columnGap: 1.25, alignItems: "baseline", py: 0.6, borderTop: `1px solid ${border}` }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: a.kind === "ready" || a.kind === "close" ? success : warning, mt: "6px" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, fontWeight: 600, lineHeight: 1.4 }}>{a.title}</Typography>
                <Typography noWrap title={a.why} sx={{ fontFamily: font, fontSize: "12px", color: textMuted }}>
                  {a.flaggedAt && moment().diff(moment(a.flaggedAt), "hours") >= 24 ? `Flagged ${moment(a.flaggedAt).fromNow()} · ` : ""}{a.why}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                {cta && (
                  <Typography onClick={() => onAct?.(a, cta)} sx={{ fontFamily: font, fontSize: "12px", fontWeight: 600, color: "#c01e2e", cursor: "pointer", whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } }}>
                    {SHORT_CTA[cta.kind] || cta.label}
                  </Typography>
                )}
                <Typography onClick={() => onDismiss?.(a.id)} title="Hide this until it changes" sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, cursor: "pointer", whiteSpace: "nowrap", "&:hover": { color: textSecondary } }}>
                  Got it
                </Typography>
              </Box>
            </Box>
          );
        })}
        {!items.length && <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textMuted }}>Nothing needs you — {first} is on track.</Typography>}

        {/* What changed: fixed since it was flagged, or already acted on. Quiet. */}
        {[...(d.resolved || []).map((r) => ({ ...r, type: "fixed" })), ...(d.acted || []).map((r) => ({ ...r, type: "acted" }))].slice(0, 3).map((r) => (
          <Typography key={`${r.type}:${r.key}`} sx={{ fontFamily: font, fontSize: "12px", color: textMuted, pt: 0.6, borderTop: `1px solid ${border}`, mt: 0.25 }}>
            {r.type === "fixed"
              ? <><Box component="span" sx={{ color: success, fontWeight: 700 }}>✓ Fixed</Box> · {r.title.replace(/^Go over /, "").replace(/ with .*$/, "")} — {r.why} · {moment(r.at).fromNow()}</>
              : <><Box component="span" sx={{ color: textSecondary, fontWeight: 700 }}>{{ propose: "Session offered", task: "Work set", message: "Messaged" }[r.how] || "Done"}</Box> · {r.title.replace(/^Go over /, "").replace(/ with .*$/, "")}{r.by ? ` · ${r.by}` : ""} · {moment(r.at).fromNow()}</>}
          </Typography>
        ))}
      </Box>

      {/* COMING UP */}
      <Box sx={{ px: 2.25, py: 1.75, borderLeft: { md: `1px solid ${border}` }, borderTop: { xs: `1px solid ${border}`, md: "none" }, backgroundColor: "#FCFBF9" }}>
        <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary, mb: 0.75 }}>Coming up</Typography>
        {upcoming.slice(0, 4).map((u, i) => (
          <Box key={u.id} sx={{ py: 0.75, borderTop: i ? `1px solid ${border}` : "none" }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
              {moment(u.at).calendar(null, { sameDay: "[Today] HH:mm", nextDay: "[Tomorrow] HH:mm", nextWeek: "ddd D MMM, HH:mm", sameElse: "ddd D MMM, HH:mm" })}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>
              {u.subjectName}{u.with ? ` · with ${u.with}` : ""}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 600, color: STATUS[u.status]?.color || textSecondary }}>{STATUS[u.status]?.text || u.status}</Typography>
            {/* What to cover, when we know. */}
            {i === 0 && prep && prep.subject === u.subject && (
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 0.25 }}>Cover: {prep.topic ? prep.title.replace(/^Cover (.*) in the session.*$/, "$1") : prep.title}</Typography>
            )}
          </Box>
        ))}
        {!upcoming.length && (
          <Box>
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textMuted }}>Nothing booked.</Typography>
            <Typography onClick={onPropose} sx={{ fontFamily: font, fontSize: "12px", fontWeight: 600, color: "#c01e2e", cursor: "pointer", mt: 0.5 }}>Propose a session</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
